<script setup lang="ts">
// Etapa 2 do saque (ENG-868): escolher gateway e digitar o valor.
//
// Limites e validação são os MESMOS do fluxo antigo (components/withdraw/
// CommomSection.vue): mínimo/máximo da marca sobrescritos pelos limites do
// jogador, e o saldo da carteira real como teto adicional. Tudo em centavos,
// que é a unidade do core.

// O valor sobrevive a ida e volta pra tela de detalhes: o casco guarda os
// centavos e devolve por aqui, como o `:initial-amount` fazia no fluxo antigo.
const { initialAmountCents = 0 } = defineProps<{
  initialAmountCents?: number;
}>();

const emit = defineEmits<{ submit: [number] }>();

const { parameters } = useParameters();
const { showMoneyFormat, walletCurrencySymbol } = useWallet();
const { userLanguage } = useUserData();
const { realWallet } = storeToRefs(useWalletStore());

const STEP_CENTS = 1000; // R$ 10,00, igual ao modal de depósito

const minWithdrawAmount = computed(
  () =>
    realWallet.value.minWithdrawAmount ||
    parameters.value?.min_withdraw_value ||
    100,
);

const maxWithdrawAmount = computed(
  () =>
    realWallet.value.maxWithdrawAmount ||
    parameters.value?.max_withdraw_value ||
    50000,
);

// `credit` vem em reais; o resto do fluxo trabalha em centavos.
const availableCents = computed(() =>
  Math.round(realWallet.value.credit * 100),
);

const availableLabel = computed(() =>
  showMoneyFormat({ value: realWallet.value.credit }),
);

const amountCents = ref<number>(initialAmountCents);

// Símbolo e separadores saem da moeda/idioma do jogador, não de 'R$'/'pt-BR'
// cravados: este campo substituiu o BaseFormInputCurrency, que já fazia isso, e
// um jogador MXN via "R$" ao lado de um saldo em MXN (o resto da tela usa
// showMoneyFormat, que respeita a moeda).
const displayValue = computed(() =>
  (amountCents.value / 100).toLocaleString(userLanguage.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
);

// Máscara de centavos: só dígitos, preenchendo da direita.
//
// Ao contrário do modal de depósito, aqui o valor digitado NÃO é cravado no
// teto: o card exige que o aviso de saque máximo apareça justamente quando o
// jogador digita acima do permitido. Travar no máximo tornaria esse aviso
// inalcançável. Quem segura é o botão (isValid), não o input.
function onAmountInput(event: Event) {
  const el = event.target as HTMLInputElement;
  const digits = el.value.replace(/\D/g, '').slice(0, 12);

  amountCents.value = Number(digits || '0');

  // Força o DOM a refletir o valor formatado: digitar uma letra não muda
  // amountCents, e sem isso o caractere ficaria visível no input.
  el.value = displayValue.value;
}

// Os botões −/+ andam dentro do permitido (não existe "errar" por clique):
// nunca passam do máximo nem do saldo disponível.
function stepAmount(delta: number) {
  const ceiling = Math.min(maxWithdrawAmount.value, availableCents.value);

  amountCents.value = Math.max(0, Math.min(ceiling, amountCents.value + delta));
}

const isBelowMin = computed(() => amountCents.value < minWithdrawAmount.value);

const isAboveMax = computed(() => amountCents.value > maxWithdrawAmount.value);

const isAboveBalance = computed(
  () => !isAboveMax.value && amountCents.value > availableCents.value,
);

const isValid = computed<boolean>(
  () =>
    amountCents.value > 0 &&
    !isBelowMin.value &&
    !isAboveMax.value &&
    !isAboveBalance.value,
);

function onSubmit() {
  if (!isValid.value) return;

  emit('submit', amountCents.value);
}
</script>

<template>
  <form
    class="wdm-amount"
    @submit.prevent="onSubmit"
  >
    <!-- Gateways ativos da rota /payments (chips compartilhados com a Etapa 1). -->
    <WithdrawModalMethodTabs />

    <!-- Campo de valor com −/+ -->
    <div class="wdm-value-row">
      <button
        aria-label="Diminuir"
        class="wdm-step-btn"
        type="button"
        @click="stepAmount(-STEP_CENTS)"
      >
        <svg
          fill="none"
          height="2"
          viewBox="0 0 14 2"
          width="14"
        >
          <path
            d="M1 1H13"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-width="2"
          />
        </svg>
      </button>

      <div class="wdm-value-field">
        <span class="wdm-value-currency">{{ walletCurrencySymbol }}</span>
        <input
          id="wdmInputCurrency"
          class="wdm-value-input"
          inputmode="numeric"
          :size="displayValue.length || 1"
          :value="displayValue"
          @input="onAmountInput"
        />
      </div>

      <button
        aria-label="Aumentar"
        class="wdm-step-btn"
        type="button"
        @click="stepAmount(STEP_CENTS)"
      >
        <svg
          fill="none"
          height="14"
          viewBox="0 0 14 14"
          width="14"
        >
          <path
            d="M7 1V13M1 7H13"
            stroke="currentColor"
            stroke-linecap="round"
            stroke-width="2"
          />
        </svg>
      </button>
    </div>

    <p class="wdm-available">
      {{ $t('account.wallet.withdraw_modal.withdraw_amount_available') }}
      <strong>{{ availableLabel }}</strong>
    </p>

    <!-- Avisos de limite. O de mínimo acompanha o campo desde o zero (Figma);
         o de máximo só aparece quando o valor digitado ultrapassa o permitido,
         e o de saldo usa a mesma faixa vermelha. -->
    <p
      v-if="isBelowMin"
      class="wdm-notice is-min"
    >
      {{ $t('account.wallet.withdraw_modal.min_notice') }}
      <strong>
        {{ showMoneyFormat({ value: minWithdrawAmount / 100 }) }}
      </strong>
    </p>

    <p
      v-else-if="isAboveMax"
      class="wdm-notice is-max"
    >
      {{ $t('account.wallet.withdraw_modal.max_notice') }}
      <strong>
        {{ showMoneyFormat({ value: maxWithdrawAmount / 100 }) }}
      </strong>
    </p>

    <p
      v-else-if="isAboveBalance"
      class="wdm-notice is-max"
    >
      {{ $t('account.wallet.withdraw_modal.form_error_4') }}
      <strong>{{ availableLabel }}</strong>
    </p>

    <!-- "Visualizar ordem" leva à mesma tela de detalhes do SACAR (SPEC 8.1).
         Compartilha o onSubmit, que é no-op quando o valor é inválido — por isso
         compartilha também o disabled, senão é clique sem resposta. -->
    <button
      class="wdm-view-order"
      :disabled="!isValid"
      type="button"
      @click="onSubmit"
    >
      <span>{{ $t('account.wallet.withdraw_modal.view_order') }}</span>
      <NuxtIcon name="arrows/angle_right" />
    </button>

    <button
      class="wdm-submit"
      :class="{ 'is-active': isValid }"
      :disabled="!isValid"
      type="submit"
    >
      {{ $t('account.wallet.withdraw_modal.withdraw_action') }}
      <template v-if="isValid">
        {{ showMoneyFormat({ value: amountCents / 100 }) }}
      </template>
    </button>
  </form>
</template>

<style scoped lang="scss">
$field-bg: #131a2c;
$field-text: #e2e3e5;
$accent: #38bdf8;
$cta: #ff3700;

.wdm-amount {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.wdm-value-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  .wdm-step-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
  }

  .wdm-value-field {
    display: inline-flex;
    align-items: baseline;
    gap: 6px;
  }

  .wdm-value-currency,
  .wdm-value-input {
    color: #fff;
    font-size: 34px;
    font-weight: 700;
    line-height: 40px;
  }

  .wdm-value-input {
    background: transparent;
    border: none;
    outline: none;
    padding: 0;
    text-align: left;
  }
}

.wdm-available {
  font-size: 12px;
  line-height: 16px;
  text-align: center;
  color: #dddee0;

  strong {
    color: $accent;
    font-weight: 700;
  }
}

.wdm-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 40px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 400;

  strong {
    font-weight: 700;
  }

  &.is-min {
    background: #29231f;
    color: #f5a524;
  }

  &.is-max {
    background: #2a1223;
    color: #ef4444;
  }
}

.wdm-view-order {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  background: $field-bg;
  color: $field-text;
  font-size: 12px;
  cursor: pointer;

  // Mesma leitura do CTA principal: sem valor válido não há ordem pra ver.
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  :deep(svg) {
    width: 14px;
    height: 14px;
  }
}

.wdm-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  height: 40px;
  border-radius: 8px;
  background: $field-bg;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: not-allowed;

  // Só com valor válido o botão vira CTA (Figma: cinza inativo → laranja com o
  // valor no rótulo).
  &.is-active {
    background: $cta;
    cursor: pointer;
  }
}
</style>
